import React, { useMemo } from 'react';
import { Box, TextV2, ButtonV2, SpacingStack } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useCreateCashFundCheckoutSessionMutation } from '../../../../CashFunds.graphql.generated';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useQueryParams } from '@shared/utils/hooks/useQueryParams';
import { CASH_FUND_GUEST_ROUTES } from '../../CashFundGuest.routes';
import { FormControl } from '../AnyAmountInput/FormControl';
import { Input } from '../AnyAmountInput/Input';
import { sanitizeContributorNote } from '../../../../utils/sanitizeNote';
import { useCashFundsTelemetry } from '../../../../CashFunds.telemetry';

export type InlineContributorFormProps = Readonly<{
  fundId: string;
  amountInMinorUnits: number;
  hostNames: string;
  currencyCode: string;
  note: string;
  inviteFullName?: string | null;
  inviteEmail?: string | null;
}>;

type ContributorFormValues = Readonly<{
  name: string;
  email: string;
}>;

export const InlineContributorForm: React.FC<InlineContributorFormProps> = ({ fundId, amountInMinorUnits, hostNames, currencyCode, note, inviteFullName, inviteEmail }) => {
  const { tq } = useTranslation('cashFundsGuest');
  const { eventHandle } = useEventInfo();
  const queryParams = useQueryParams();
  const telemetry = useCashFundsTelemetry();
  const guestKey = typeof queryParams.guestKey === 'string' ? queryParams.guestKey : null;
  const [createCheckoutSession, { loading }] = useCreateCashFundCheckoutSessionMutation();

  const validationSchema = useMemo(() => {
    return Yup.object<ContributorFormValues>({
      name: Yup.string().required(tq`Name is required`),
      email: Yup.string()
        .email(tq`Please enter a valid email address`)
        .required(tq`Email is required`),
    });
  }, [tq]);

  const formik = useFormik<ContributorFormValues>({
    initialValues: {
      name: inviteFullName || '',
      email: inviteEmail || '',
    },
    validationSchema,
    onSubmit: async values => {
      telemetry.continueToCheckoutClicked(fundId, amountInMinorUnits, values.email.trim(), sanitizeContributorNote(note) || '', currencyCode);

      try {
        const successUrl = window.location.origin + CASH_FUND_GUEST_ROUTES.success.goTo(eventHandle || '', fundId);
        const cancelUrl = window.location.origin + window.location.pathname;
        const { data } = await createCheckoutSession({
          variables: {
            input: {
              cashFundId: fundId,
              amountInMinorUnits,
              contributorFullName: values.name.trim(),
              contributorEmail: values.email.trim(),
              contributorMessage: sanitizeContributorNote(note),
              guestKey: guestKey || null,
              successUrlTemplate: `${successUrl}?paymentId={paymentId}`,
              cancelUrlTemplate: cancelUrl,
            },
          },
        });

        const session = data?.createCashFundCheckoutSession;

        if (session?.__typename === 'CashFundGeneralError') {
          telemetry.mutationError('createCashFundCheckoutSession', new Error(session.code || 'Failed to create checkout session'), fundId);
          formik.setStatus(tq`Failed to create checkout session. Please try again.`);
          return;
        }

        if (session?.__typename === 'CashFundCheckoutSession' && session.redirectUrl) {
          window.location.href = session.redirectUrl;
        } else {
          telemetry.mutationError('createCashFundCheckoutSession', new Error('Invalid checkout session response'), fundId);
          formik.setStatus(tq`Failed to create checkout session. Please try again.`);
        }
      } catch (err) {
        telemetry.mutationError('createCashFundCheckoutSession', err instanceof Error ? err : new Error('Unexpected error'), fundId);
        formik.setStatus(tq`An unexpected error occurred. Please try again.`);
      }
    },
  });

  const hasNameError = !!(formik.touched.name && formik.errors.name);
  const hasEmailError = !!(formik.touched.email && formik.errors.email);
  const nameErrorId = `name-error-${fundId}`;
  const emailErrorId = `email-error-${fundId}`;

  return (
    <Box>
      <TextV2
        typographyVariant="label1"
        marginBottom={3}
      >
        {tq`Please share your details to help ${hostNames} track your contribution.`}
      </TextV2>

      <form
        onSubmit={formik.handleSubmit}
        aria-busy={loading}
      >
        <SpacingStack spacing={5}>
          <FormControl
            label={tq`Name`}
            error={hasNameError ? formik.errors.name : undefined}
            isInvalid={hasNameError}
            overrides={{
              Hint: {
                props: hasNameError ? { id: nameErrorId, role: 'alert' } : {},
              },
            }}
          >
            <Input
              {...formik.getFieldProps('name')}
              placeholder={tq`First and last name`}
              disabled={loading}
              aria-invalid={hasNameError}
              aria-describedby={hasNameError ? nameErrorId : undefined}
              aria-label={tq`Name`}
            />
          </FormControl>

          <FormControl
            label={tq`Email`}
            error={hasEmailError ? formik.errors.email : undefined}
            isInvalid={hasEmailError}
            overrides={{
              Hint: {
                props: hasEmailError ? { id: emailErrorId, role: 'alert' } : {},
              },
            }}
          >
            <Input
              {...formik.getFieldProps('email')}
              type="email"
              placeholder={tq`Email`}
              disabled={loading}
              aria-invalid={hasEmailError}
              aria-describedby={hasEmailError ? emailErrorId : undefined}
              aria-label={tq`Email`}
            />
          </FormControl>
        </SpacingStack>

        {formik.status && (
          <TextV2
            role="alert"
            typographyVariant="body2"
            color="red9"
            marginTop={4}
          >
            {formik.status}
          </TextV2>
        )}

        <ButtonV2
          variant="solid"
          intent="neutral"
          shape="rounded"
          size="lg"
          width="100%"
          type="submit"
          loading={loading}
          disabled={loading}
          marginTop={5}
          aria-busy={loading}
        >
          {tq`Continue to Checkout`}
        </ButtonV2>
      </form>
    </Box>
  );
};
